<script lang="ts">
    import Icon from '@iconify/svelte'
    import { isEqual, orderBy } from 'lodash-es'

    import PopEntry from 'hydria/components/PopEntry.svelte'
    import { uuid } from 'hydria/document_helpers'
    import { gettext as _, resolve, type TGettext } from 'hydria/services/gettext'
    import { makeSortString } from 'hydria/utils/strings'
    import { tick } from 'svelte'

    interface Props {
        saved: string[]
        value: string[]
        disabled?: boolean
        label: TGettext
        isValid?: any
        isTagValid?: any
        save?: (newValue: string[]) => void
    }

    let { saved: savedValue, value: currentValue = $bindable(), disabled = false, label, isValid = (value: string[]) => true, isTagValid = (value: string) => true, save = (v) => {} }: Props = $props()

    let savedSorted: string[] = $derived(savedValue ? orderBy([...new Set(savedValue)], [(v) => makeSortString(v)], ['asc']) : [])
    let currentSorted: string[] = $state([])

    let newTagValue: string = $state('')

    function importCurrent(value: string[]) {
        currentSorted = value ? orderBy([...new Set(value)], [(v) => makeSortString(v)], ['asc']) : []
    }

    let savedIsValid = isValid(savedValue)

    let isOpen = $state(false)

    function open() {
        newTagValue = ''
        isOpen = true
    }
    function close() {
        isOpen = false
    }

    function addNewTag() {
        currentSorted.push(newTagValue)
        currentSorted = orderBy([...new Set(currentSorted)], [(v) => makeSortString(v)], ['asc'])
        keepCurrentSorted()
        isOpen = false
    }

    function keepCurrentSorted() {
        currentValue = [...currentSorted]
        save(currentValue)
    }

    const id = uuid('t')
    let element: HTMLElement | undefined = $state()

    $effect(() => {
        currentValue
        tick().then(() => {
            importCurrent(currentValue)
        })
    })
    let currentLowercasedSet = $derived(new Set(currentSorted.map((o) => o.toLocaleLowerCase())))
    let isPristine = $derived(isEqual(currentValue, savedValue))
    let statusIcon: string = $derived(isPristine ? (savedIsValid ? 'mdi:check-circle' : 'mdi:alert') : isValid(currentValue) ? 'mdi:undo' : 'mdi:undo')
    let statusClass: string = $derived(isPristine ? (savedIsValid ? 'btn-link' : 'btn-danger') : isValid(currentValue) ? 'btn-outline-warning' : 'btn-outline-danger')
</script>

<style>
    .tag-list {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key == 'Escape' && isOpen) close()
        else if (e.key == 'Enter' && isTagValid(newTagValue) && isOpen) addNewTag()
    }}
/>

<div class="row g-3 align-items-baseline">
    <div class="col-2 text-end"><label aria-hidden="true" for={id} class="control-label" onclick={() => open()}>{$resolve(label)}</label></div>
    <div class="col">
        <div class="tag-list">
            {#each currentSorted as tag}
                <div class="tag">
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-primary"
                        data-tooltip={$_('Enlever « {tag} »', { tag: tag })}
                        onclick={() => {
                            currentSorted = currentSorted.filter((o) => o !== tag)
                            keepCurrentSorted()
                        }}>{tag}</button
                    >
                </div>
            {/each}
        </div>
        {#if isOpen}
            <PopEntry
                close={() => close()}
                mounted={() => {
                    if (element) element.focus()
                }}
            >
                <div class="card">
                    <div class="card-header text-bg-primary">{$_('Ajouter un tag')}</div>
                    <div class="card-body">
                        <input type="text" class="form-control" bind:value={newTagValue} bind:this={element} />
                    </div>
                    <div class="card-footer d-flex gap-2 justify-content-end">
                        <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => close()}>{$_('Fermer')}</button>
                        <button type="button" class="btn btn-sm btn-primary" disabled={!isTagValid(newTagValue) || currentLowercasedSet.has(newTagValue.toLocaleLowerCase())} onclick={() => addNewTag()}>{$_('Appliquer')}</button>
                    </div>
                </div>
            </PopEntry>
        {/if}
    </div>
    <div class="col-auto text-end">
        <button
            type="button"
            class="btn btn-sm {statusClass}"
            disabled={isPristine}
            onclick={() => {
                currentValue = [...savedValue]
                importCurrent(currentValue)
                save(currentValue)
            }}><Icon inline icon={statusIcon} /></button
        >
        <button type="button" class="btn btn-sm btn-outline-secondary" {disabled} onclick={() => open()}><Icon inline icon="mdi:edit" /></button>
    </div>
</div>
